import React, { useState, useEffect } from 'react';

const LAST_STATUS_API = '/api/device-status';

const STYLES = `
  @keyframes fadeUp   { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin     { to{transform:rotate(360deg)} }
  @keyframes fillBar  { from{width:0} to{width:var(--w)} }
  .rf-card { transition:box-shadow .2s,transform .18s; }
  .rf-card:hover { box-shadow:0 10px 36px rgba(0,0,0,.1)!important; transform:translateY(-2px); }
`;

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}

function getRefreshStatus(days) {
  if (days == null) return { label:'Unknown', color:'#8E8E93', pct:null };
  if (days <= 30)   return { label:'Fresh',   color:'#34C759', pct:Math.round((days/30)*100) };
  if (days <= 90)   return { label:'Due Soon', color:'#FF9F0A', pct:Math.round((days/90)*100) };
  return               { label:'Overdue',  color:'#FF3B30', pct:100 };
}

function KPICard({ label, value, color, icon, sub, delay='0s' }) {
  return (
    <div style={{
      background:'#fff', borderRadius:16, padding:'16px 18px',
      border:`1.5px solid ${color}22`, boxShadow:'0 2px 10px rgba(0,0,0,.05)',
      position:'relative', overflow:'hidden',
      animation:`fadeUp .35s ease ${delay} both`,
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background:color, borderRadius:'16px 16px 0 0' }}/>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div style={{ fontSize:10, fontWeight:700, color:'#8E8E93', letterSpacing:.4, textTransform:'uppercase' }}>
          {label}
        </div>
        <span style={{ fontSize:20 }}>{icon}</span>
      </div>
      <div style={{ fontSize:30, fontWeight:900, color:'#1c1c1e', letterSpacing:-.8, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:color, fontWeight:600, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function DeviceCard({ device, idx }) {
  const reg    = device._registration || {};
  const days   = daysSince(device._lastRefreshment);
  const status = getRefreshStatus(days);

  return (
    <div className="rf-card" style={{
      background:'#fff', borderRadius:18, padding:'20px',
      border:`1.5px solid ${status.color}18`,
      boxShadow:'0 2px 14px rgba(0,0,0,.06)',
      animation:`fadeUp .35s ease ${idx*.07}s both`,
      position:'relative', overflow:'hidden',
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background:`linear-gradient(90deg,${status.color},${status.color}66)`,
        borderRadius:'18px 18px 0 0' }}/>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:46, height:46, borderRadius:13, flexShrink:0,
            background:`linear-gradient(135deg,${status.color}22,${status.color}11)`,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
            ♻️
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:'#1c1c1e' }}>
              {device.friendlyname || 'Unknown Device'}
            </div>
            <div style={{ fontSize:10, color:'#8E8E93', fontFamily:'monospace', marginTop:2 }}>
              {device.macid?.toUpperCase() || '—'}
            </div>
          </div>
        </div>
        <span style={{ fontSize:11, fontWeight:800, padding:'5px 12px', borderRadius:20,
          background:`${status.color}15`, color:status.color, letterSpacing:.4 }}>
          {status.label}
        </span>
      </div>

      {/* Refreshment timeline bar */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:10,
          color:'#8E8E93', marginBottom:5 }}>
          <span>Refreshment cycle (90 days)</span>
          <span style={{ color:status.color, fontWeight:700 }}>
            {days != null ? `${days} days elapsed` : 'No data'}
          </span>
        </div>
        <div style={{ background:'rgba(0,0,0,.06)', borderRadius:999, height:7, overflow:'hidden' }}>
          <div style={{ height:'100%', borderRadius:999,
            background:`linear-gradient(90deg,${status.color},${status.color}88)`,
            width:`${Math.min(100, status.pct||0)}%`, transition:'width .7s ease',
            boxShadow:`0 0 10px ${status.color}44` }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
          <span style={{ fontSize:8, color:'#C7C7CC' }}>Last refresh</span>
          <span style={{ fontSize:8, color:'#C7C7CC' }}>30d</span>
          <span style={{ fontSize:8, color:'#C7C7CC' }}>60d</span>
          <span style={{ fontSize:8, color:'#C7C7CC' }}>90d</span>
        </div>
      </div>

      {/* Info grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        {[
          ['Last Refreshment', formatDate(device._lastRefreshment)],
          ['Days Since',       days != null ? `${days} days ago` : '—'],
          ['Customer',         reg.userName || reg.custName || '—'],
          ['Phone',            reg.phone || '—'],
          ['Location',         reg.location || '—'],
          ['Model',            reg.modelNo || '—'],
        ].map(([label, value]) => (
          <div key={label} style={{ padding:'9px 12px', background:'rgba(0,0,0,.025)', borderRadius:11 }}>
            <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, letterSpacing:.3, textTransform:'uppercase', marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:12, color:'#1c1c1e', fontWeight:700, wordBreak:'break-word' }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RefreshmentsPage({ devices }) {
  const [enriched, setEnriched] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!devices || devices.length === 0) { setLoading(false); return; }
    setLoading(true);
    const promises = devices.map(dev => {
      const mac = (dev.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase();
      if (!mac) return Promise.resolve({ ...dev, _registration:{}, _lastRefreshment:null });
      return fetch(`${LAST_STATUS_API}?macid=${mac}`)
        .then(r => r.ok ? r.json() : {})
        .then(json => {
          const reg = json.registration || {};
          const lastRefresh = json.latest_recharge?.updated_at || json.latest_recharge?.data?.T
            || reg.last_refreshment || null;
          return { ...dev,
            _registration: { userName:reg.Enduser_name||reg.cust_name||'', phone:reg.phone_no||'',
              location:reg.location||'', modelNo:reg.model_no||'',
              custName:reg.cust_name==='Livpure'?'Havells':(reg.cust_name||'') },
            _lastRefreshment: lastRefresh };
        })
        .catch(() => ({ ...dev, _registration:{}, _lastRefreshment:null }));
    });
    Promise.all(promises).then(r => { setEnriched(r); setLoading(false); });
  }, [devices]);

  const filtered = enriched.filter(d => {
    const reg = d._registration||{};
    return !!(d._lastRefreshment||d.friendlyname||reg.userName||reg.location||reg.modelNo);
  });
  const total   = filtered.length;
  const fresh   = filtered.filter(d => { const n=daysSince(d._lastRefreshment); return n!=null&&n<=30; }).length;
  const due     = filtered.filter(d => { const n=daysSince(d._lastRefreshment); return n!=null&&n>30&&n<=90; }).length;
  const overdue = filtered.filter(d => { const n=daysSince(d._lastRefreshment); return n!=null&&n>90; }).length;

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:960 }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <img src="/havells-logo.png" alt="Havells" style={{ height:40, objectFit:'contain', marginBottom:10, display:'block' }}/>
        <h1 style={{ fontSize:26, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, margin:0 }}>
          ♻️ Refreshments
        </h1>
        <p style={{ color:'#8E8E93', fontSize:13, marginTop:4, margin:'4px 0 0' }}>
          Filter replacement schedule · {total} device{total!==1?'s':''} tracked
          {overdue > 0 && <span style={{ color:'#FF3B30', fontWeight:700 }}> · {overdue} overdue</span>}
        </p>
      </div>

      {/* KPI row — 2 cards only */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24, maxWidth:400 }}>
        <KPICard label="Total Devices" value={total} color="#007AFF" icon="📊" delay=".0s"/>
        <KPICard label="0 – 2 Yrs"     value={total} color="#34C759" icon="♻️" sub="Active devices" delay=".05s"/>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#8E8E93' }}>
          <div style={{ width:28,height:28,borderRadius:'50%',border:'3px solid rgba(52,199,89,.15)',
            borderTopColor:'#34C759',animation:'spin .7s linear infinite',margin:'0 auto 14px'}}/>
          <p style={{ fontSize:13 }}>Loading device data…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'#fff',borderRadius:20,padding:'56px 24px',textAlign:'center',
          border:'1px solid rgba(0,0,0,.06)',boxShadow:'0 2px 10px rgba(0,0,0,.04)' }}>
          <div style={{ fontSize:48, marginBottom:14 }}>♻️</div>
          <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', marginBottom:6 }}>No devices found</div>
          <div style={{ fontSize:13, color:'#8E8E93' }}>No registered devices with refreshment data</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(420px,1fr))', gap:14 }}>
          {filtered.map((dev, idx) => (
            <DeviceCard key={dev.macid||idx} device={dev} idx={idx}/>
          ))}
        </div>
      )}
    </div>
  );
}
